import { useEffect, useState } from 'react';
import styled from 'styled-components';
import DefaultButton from 'components/button/DefaultButton';
import { color } from 'asserts/styles/colors';
import { ReactComponent as IconArrow } from 'asserts/icons/ic_chevron_up_table.svg';
import { font } from 'asserts/styles/fonts';
import DefaultInput from 'components/input/DefaultInput';
import { useMutation, useQuery } from '@tanstack/react-query';
import { QueryKeys, restfetcher } from 'queryClient';
import { useAtomValue } from 'jotai';
import companyInforAtom from 'atom/companyInfoAtom';
import DOMPurify from 'dompurify';
import { NoDataWrapper } from './Reference';
import NoSearchData from './NoSearchData';
import Paginav from 'components/paginav/Pagination';
import { useLocation, useNavigate } from 'react-router-dom';
import { DataWrapper } from 'components/skeleton/FAQSkeleton';

interface FAQProps {
  boardData: BoardListData[];
  activeTabKey: string;
}

interface ContentToggleState {
  [key: number]: boolean;
}

const FAQ = ({ boardData }: FAQProps) => {
  const navigate = useNavigate();
  const location = useLocation();

  const companyInfor = useAtomValue(companyInforAtom);
  const urlSearchParams = new URLSearchParams(location.search);
  const [selectedButtonIndex, setSelectedButtonIndex] = useState(0);
  const [pageNumber, setPageNumber] = useState(0);

  const [selectedMenuIdx, setSelectedMenuIdx] = useState<number>(
    Number(urlSearchParams.get('menu'))
  );

  const [searchInputValue, setSearchInputValue] = useState('');

  const handleButtonClick = (index: number) => {
    setSelectedButtonIndex(index);
    setSearchInputValue('');
    setPageNumber(0);
    setSelectedMenuIdx(boardData[index].menuidx);
    setContentToggle((prevState) => ({
      ...prevState,
      [index]: !prevState[index]
    }));
  };

  const handleChangeSearchInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setSearchInputValue(value);
  };

  useEffect(() => {
    const urlSearchParams = new URLSearchParams();

    urlSearchParams.set('menu', selectedMenuIdx.toString());
    urlSearchParams.set('submenu', '');

    navigate(`${window.location.pathname}?${urlSearchParams.toString()}`, { replace: true });

    setContentToggle({});
  }, [selectedMenuIdx, pageNumber, navigate]);

  useEffect(() => {
    setSelectedMenuIdx(boardData[selectedButtonIndex]?.menuidx || 0);
  }, [boardData, selectedButtonIndex]);

  const params = {
    categoryidx: companyInfor.categoryidx, // companyInfor
    countrycode: 'KR',
    menuidx: selectedMenuIdx,
    submenuidx: 0,
    keyword: searchInputValue,
    page: pageNumber,
    pagesize: 10
  };

  const boardSearchListApi = useQuery({
    queryKey: [QueryKeys.BOARD_SEARCH_FAQ, params],
    queryFn: () =>
      restfetcher({
        method: 'POST',
        path: 'biz/board/search',
        params: params
      })
  });

  const { data: boardList } = boardSearchListApi;

  const boardSearchList: BoardSearchList[] = boardList && boardList.data?.list;

  useEffect(() => {
    if (companyInfor.categoryidx !== 0) {
      boardSearchListApi.refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyInfor.categoryidx, selectedMenuIdx, pageNumber]);

  const handleSearch = (
    searchValue: string,
    event?:
      | React.ChangeEvent<HTMLInputElement>
      | React.MouseEvent<HTMLElement, MouseEvent>
      | React.KeyboardEvent<HTMLInputElement>,
    info?: { source?: 'clear' | 'input' }
  ) => {
    if (info?.source === 'input') {
      boardSearchListApi.refetch();
    }
  };

  const [contentToggle, setContentToggle] = useState<ContentToggleState>({});

  const increaseViewCountApi = useMutation({
    mutationFn: (param: number) =>
      restfetcher({
        method: 'POST',
        path: 'biz/board/increase/view',
        params: {
          boardidx: param
        }
      })
  });

  const toggleContent = (boardno: number, boardidx: number) => {
    setContentToggle((prevState) => {
      const newState: ContentToggleState = {};
      newState[boardno] = !prevState[boardno];

      Object.keys(prevState).forEach((key) => {
        const boardidx = parseInt(key);
        if (boardidx !== boardno) {
          newState[boardidx] = false;
        }
      });

      return newState;
    });

    if (boardidx !== 0) {
      increaseViewCountApi.mutate(boardidx);
    }
  };

  const handlePageChange = (page: number) => {
    setPageNumber(page);
  };

  return (
    <Wrapper>
      <ButtonWrapper>
        {boardData.map((item, index) => (
          <DefaultButton
            key={index}
            text={item.menuname}
            type={selectedButtonIndex === index ? 'primary' : 'default'}
            onClick={() => handleButtonClick(index)}
          />
        ))}
      </ButtonWrapper>
      <SearchWrapper>
        <DefaultInput
          name="search"
          search
          placeholder="입력하세요"
          height="middle"
          onChange={handleChangeSearchInput}
          onSearch={handleSearch}
        />
      </SearchWrapper>
      <>
        {boardList?.result === 'success' ? (
          boardSearchList && boardSearchList.length !== 0 ? (
            boardSearchList.map((item, index) => {
              return (
                <ContentWrapper
                  $visible={contentToggle[item.boardno]}
                  key={index}
                  onClick={() => toggleContent(item.boardno, item.boardidx)}
                >
                  <TitleWrapper>
                    <h6>{item.boardno}</h6>
                    <h4>{item.title}</h4>
                    <IconArrowWrapper
                      rotate={contentToggle[item.boardno] ? 'rotate(0deg)' : 'rotate(180deg)'}
                    >
                      <IconArrow />
                    </IconArrowWrapper>
                  </TitleWrapper>
                  <Contents $visible={contentToggle[item.boardno]}>
                    {item.contents && (
                      <div
                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(item.contents) }}
                      />
                    )}
                  </Contents>
                </ContentWrapper>
              );
            })
          ) : (
            <NoDataWrapper>
              <NoSearchData />
            </NoDataWrapper>
          )
        ) : (
          <InnerWrppaer>
            {Array(5)
              .fill(null)
              .map((_, index) => (
                <DataWrapper key={index}>
                  <div></div>
                  <div></div>
                </DataWrapper>
              ))}
          </InnerWrppaer>
        )}
        {boardList?.data?.totalPages > 1 && (
          <PaginationWrapper>
            <Paginav
              total={boardList.data?.totalElements}
              currentPage={pageNumber}
              pageSize={boardList.data?.size}
              onPageChange={handlePageChange}
            />
          </PaginationWrapper>
        )}
      </>
    </Wrapper>
  );
};

export default FAQ;

const Layout = styled.div`
  display: flex;
  justify-content: center;
  width: 890px;
  margin: 0 auto;
`;

const Wrapper = styled(Layout)`
  flex-direction: column;
  color: ${color.navyLight};
  margin-bottom: 128px;
`;

const SearchWrapper = styled.div`
  margin-right: auto;
  padding-bottom: 32px;

  & > div {
    width: 320px;
  }
`;

const ButtonWrapper = styled(Layout)`
  justify-content: flex-start;
  padding: 24px 0 48px;
  gap: 8px;
`;

const TitleWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 24px;

  & svg {
    width: 24px;
    height: 24px;
    transition: transform 0.3s;
  }

  & h6 {
    color: ${color.gray600};
    ${font.caption1_regular};
  }

  & h4 {
    width: 100%;
    color: ${color.black2};
    ${font.h4_medium};
  }
`;

const ContentWrapper = styled.div<{ $visible: boolean }>`
  display: flex;
  flex-direction: column;
  margin-bottom: 8px;
  padding: 24px 32px;
  border-radius: 12px;
  border: 1.5px solid ${color.gray200};
  background: ${color.white};
  cursor: pointer;
  box-shadow: ${({ $visible }) => ($visible ? `0px 0px 16px 0px ${color.opacityBlack02}` : 'none')};
`;

const Contents = styled.div<{ $visible: boolean }>`
  color: ${color.gray600};
  ${font.body_regular};
  height: ${({ $visible }) => ($visible ? 'auto' : '0')};
  overflow: hidden;
  opacity: ${({ $visible }) => ($visible ? '1' : '0')};
  transition: height 0.3s, opacity 0.2s ease-in-out;

  & div {
    margin-top: 24px;
  }

  & img {
    width: 100% !important;
    height: 100%;
  }
`;

const IconArrowWrapper = styled.div<{ rotate: string }>`
  display: flex;
  align-items: center;
  transform: ${({ rotate }) => rotate};
  transition: transform 0.3s;
`;

export const PaginationWrapper = styled.div`
  margin: 48px 0 128px;
  display: flex;
  justify-content: end;
`;

const InnerWrppaer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;
